import { useEffect, useState } from "react";
import { Form, Link } from "react-router";
import type { VariantProps } from "class-variance-authority";
import { Camera, ImagePlus } from "lucide-react";
import type { Picture } from "@repo/shared-types";
import { timeLeft } from "@/lib/images";
import { Badge } from "@/components/ui/badge";
import { PictureButton } from "@/components/form";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * A workout's photo on the web (7g): kept 24 hours from the first, shown on
 * the result's own page, in the log and on your own result, and marked on a
 * board line. Shared, like the notes and feels, by the workout's one toggle;
 * hidden from a space by its moderators until they put it back.
 */

/**
 * A file box dressed as a button: the label is what is tapped, and the ring
 * shows while the input inside it has the keyboard's focus. shadcn's button,
 * outlined unless asked otherwise.
 */
export const fileButton = ({ variant = "outline", size = "lg" }: VariantProps<typeof buttonVariants> = {}) =>
  cn(
    buttonVariants({ variant, size }),
    "cursor-pointer has-[:focus-visible]:border-ring has-[:focus-visible]:ring-3 has-[:focus-visible]:ring-ring/30",
  );

/**
 * A photo as its reader may see it: the picture while it has a file, "Photo
 * expired" once its 24 hours are over, and for its athlete — or a moderator —
 * the space's hiding.
 */
export function PhotoView({
  photo,
  alt,
  spaceName,
  size = "large",
}: {
  photo: Picture | null;
  alt: string;
  spaceName: string | null;
  size?: "large" | "thumbnail";
}) {
  if (!photo || photo.status === "removed") return null;
  if (photo.status === "expired" || !photo.url) {
    return <p className="text-sm text-muted-foreground">Photo expired</p>;
  }
  const frame = size === "thumbnail" ? "h-24 w-24 object-cover" : "max-h-[32rem] w-full object-contain";
  return (
    <figure className="space-y-1">
      <img
        src={photo.url}
        alt={alt}
        width={photo.width ?? undefined}
        height={photo.height ?? undefined}
        loading="lazy"
        className={`rounded-2xl bg-muted ${frame}`}
      />
      {photo.hiddenAt && (
        <figcaption className="text-xs text-warning">Hidden by {spaceName ?? "the space"}</figcaption>
      )}
    </figure>
  );
}

/**
 * The photo of your own result, chosen while recording: adding, replacing or
 * taking it down is kept on the page, marked as not sent, and goes with the
 * rest — Register workout, then Save changes, in the bar at the bottom. A
 * photo alone is something to send. Nothing once its 24 hours are over: the
 * window opens once, from the first.
 */
export function PhotoField({
  photo,
  file,
  onFile,
  removing,
  onRemoving,
  notSent,
  spaceName,
  busy,
}: {
  /** The photo saved with the result, if any. */
  photo: Picture | null;
  /** A picture chosen and not sent yet. */
  file: File | null;
  onFile: (file: File | null) => void;
  /** Whether the saved photo is to be taken down when the result is sent. */
  removing: boolean;
  onRemoving: (removing: boolean) => void;
  /** What a change not sent yet is called: "not registered yet", "not saved yet". */
  notSent: string;
  spaceName: string | null;
  busy: boolean;
}) {
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(() => {
    let url: string | null = null;
    try {
      // A preview is a nicety: where a file cannot be shown, say so without one.
      if (file) url = URL.createObjectURL(file);
    } catch {
      url = null;
    }
    setPreview(url);
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [file]);

  const left = timeLeft(photo?.expiresAt ?? null);
  const closed = photo !== null && left === null;
  const ready = photo?.status === "ready" && !closed;
  if (closed) {
    return (
      <p className="text-sm text-muted-foreground">
        {photo?.status === "removed" ? "The 24 hours for a photo are over." : "Photo expired"}
      </p>
    );
  }

  const choose = (label: string) => (
    <PictureButton
      size="sm"
      disabled={busy}
      onChange={(change) => {
        const chosen = change.target.files?.[0] ?? null;
        if (chosen) {
          onFile(chosen);
          onRemoving(false);
        }
        change.target.value = "";
      }}
    >
      <ImagePlus aria-hidden="true" />
      {label}
    </PictureButton>
  );
  return (
    <div className="space-y-3">
      {file ? (
        preview && <img src={preview} alt="Your photo, not sent yet" className="h-24 w-24 rounded-2xl object-cover" />
      ) : (
        ready && (
          <div className={removing ? "opacity-40" : ""}>
            <PhotoView photo={photo} alt="Your photo" spaceName={spaceName} size="thumbnail" />
          </div>
        )
      )}
      <div className="flex flex-wrap items-center gap-2">
        {file ? (
          <>
            <PendingMark>{notSent}</PendingMark>
            {choose("Replace photo")}
            <Button type="button" variant="outline" size="sm" onClick={() => onFile(null)}>
              {ready ? "Keep the one saved" : "Remove photo"}
            </Button>
          </>
        ) : removing ? (
          <>
            <PendingMark>will be removed</PendingMark>
            <Button type="button" variant="outline" size="sm" onClick={() => onRemoving(false)}>
              Undo
            </Button>
          </>
        ) : (
          <>
            {choose(ready ? "Replace photo" : "Add a photo")}
            {ready && (
              <Button type="button" variant="outline" size="sm" onClick={() => onRemoving(true)}>
                Remove photo
              </Button>
            )}
          </>
        )}
        {left && <span className="text-xs text-muted-foreground">{left} of its 24 hours</span>}
      </div>
    </div>
  );
}

/** What a photo chosen and not sent says beside it. */
function PendingMark({ children }: { children: string }) {
  return (
    <Badge variant="secondary" className="bg-warning-muted text-warning">
      {children}
    </Badge>
  );
}

/**
 * The mark on a board line whose workout has a photo the line may show,
 * leading to the result's page, where it is. Relative to the event's page.
 */
export function PhotoMark({ to }: { to: string }) {
  return (
    <Link
      to={to}
      aria-label="Photo"
      title="Photo"
      className="ml-1 inline-flex align-middle text-muted-foreground hover:text-foreground"
    >
      <Camera aria-hidden="true" className="size-4" />
    </Link>
  );
}

/**
 * A moderator hiding a workout's photo from the space, or putting it back.
 * The rest of the result stays on the boards.
 */
export function HidePhoto({ hidden, busy }: { hidden: boolean; busy: boolean }) {
  return (
    <Form method="post">
      <input type="hidden" name="intent" value={hidden ? "unhide-photo" : "hide-photo"} />
      <Button type="submit" variant="outline" size="lg" disabled={busy}>
        {hidden ? "Put the photo back" : "Hide the photo"}
      </Button>
    </Form>
  );
}
